import { NavLink, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard, Users, Store, FolderOpen, Package,
  ShoppingCart, Truck, Tag, Star, Bell, LogOut, ChevronRight,
  BarChart3, DollarSign, RefreshCw, PackageCheck, CreditCard,
  Settings, Gift, ClipboardList, Image as ImageIcon, FileText,
} from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'
import { ROUTES } from '@/constants/routes'

// Groups with dividers
const NAV_GROUPS = [
  {
    items: [
      { key: 'dashboard',  icon: LayoutDashboard, route: ROUTES.DASHBOARD },
      { key: 'analytics',  icon: BarChart3,        route: ROUTES.ANALYTICS },
    ],
  },
  {
    label: 'إدارة الأشخاص',
    items: [
      { key: 'users',   icon: Users,       route: ROUTES.USERS },
      { key: 'vendors', icon: Store,       route: ROUTES.VENDORS },
      { key: 'plans',   icon: CreditCard,  route: ROUTES.PLANS },
    ],
  },
  {
    label: 'المنتجات والفئات',
    items: [
      { key: 'categories', icon: FolderOpen, route: ROUTES.CATEGORIES },
      { key: 'brands',     icon: Tag,        route: ROUTES.BRANDS },
      { key: 'products',   icon: Package,    route: ROUTES.PRODUCTS },
    ],
  },
  {
    label: 'الطلبات والشحن',
    items: [
      { key: 'orders',    icon: ShoppingCart, route: ROUTES.ORDERS },
      { key: 'returns',   icon: RefreshCw,    route: ROUTES.RETURNS },
      { key: 'shipping',  icon: Truck,        route: ROUTES.SHIPPING },
      { key: 'shipments', icon: PackageCheck, route: ROUTES.SHIPMENTS },
    ],
  },
  {
    label: 'المالية',
    items: [
      { key: 'payouts',    icon: DollarSign, route: ROUTES.PAYOUTS },
      { key: 'promotions', icon: Tag,        route: ROUTES.PROMOTIONS },
    ],
  },
  {
    label: 'التفاعل',
    items: [
      { key: 'reviews',       icon: Star,           route: ROUTES.REVIEWS },
      // { key: 'chat',          icon: MessageSquare,  route: ROUTES.CHAT }, // CHAT_DISABLED
      { key: 'notifications', icon: Bell,           route: ROUTES.NOTIFICATIONS },
    ],
  },
  {
    label: 'الإعدادات',
    items: [
      { key: 'banners',      icon: ImageIcon,     route: ROUTES.BANNERS },
      { key: "earlyRegistrations", icon: FileText, route: ROUTES.EARLY_REGISTRATIONS },

      { key: 'blog',         icon: FileText,      route: ROUTES.BLOG },
      { key: 'loyalty',       icon: Gift,          route: ROUTES.LOYALTY },
      { key: 'activityLogs', icon: ClipboardList,  route: ROUTES.ACTIVITY_LOGS },
      { key: 'settings',     icon: Settings,       route: ROUTES.SETTINGS },
    ],
  },
]

import { usePlatformSettings } from '@/contexts/PlatformSettingsContext'

export default function Sidebar({ collapsed, onToggle }) {
  const { t } = useTranslation()
  const { user, logout } = useAuth()
  const location = useLocation()
  const { settings } = usePlatformSettings()

  return (
    <aside
      className="flex flex-col h-full transition-all duration-300"
      style={{
        width: collapsed ? 72 : 256,
        background: '#000666',
        boxShadow: '4px 0 24px rgba(0,0,0,0.15)',
      }}
    >
      {/* Logo */}
      <div
        className="flex items-center px-4 py-5 gap-3 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}
      >
        {settings?.logo ? (
          <img 
            src={settings.logo} 
            alt="Logo" 
            className="w-10 h-10 rounded-xl object-contain bg-white/10" 
          />
        ) : (
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: '#FF6D00', boxShadow: '0 4px 12px rgba(255,109,0,0.4)' }}
          >
            <span
              className="text-white font-black text-sm"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              H
            </span>
          </div>
        )}
        {!collapsed && (
          <div className="overflow-hidden">
            <div
              className="text-white font-black text-lg leading-none"
              style={{ fontFamily: 'Cairo, sans-serif' }}
            >
              حاجاتنا
            </div>
            <div
              className="text-xs mt-0.5"
              style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Tajawal, sans-serif' }}
            >
              لوحة الإدارة
            </div>
          </div>
        )}
        <button
          onClick={onToggle}
          className="mr-auto p-1.5 rounded-lg transition-colors hover:bg-white/10 flex-shrink-0"
          style={{ color: 'rgba(255,255,255,0.5)' }}
        >
          <ChevronRight
            size={16}
            style={{
              transform: collapsed ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        {NAV_GROUPS.map((group, gi) => (
          <div key={gi} style={{ marginBottom: 4 }}>
            {/* Group label */}
            {group.label && !collapsed && (
              <div style={{
                fontSize: 9, fontWeight: 800, letterSpacing: '0.08em',
                color: 'rgba(255,255,255,0.3)', padding: '10px 10px 4px',
                fontFamily: 'Tajawal, sans-serif', textTransform: 'uppercase',
              }}>
                {group.label}
              </div>
            )}
            {group.label && collapsed && gi > 0 && (
              <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '6px 4px' }} />
            )}
            <div className="space-y-0.5">
              {group.items.map(({ key, icon: Icon, route }) => {
                const isActive = route === ROUTES.DASHBOARD
                  ? location.pathname === '/'
                  : location.pathname.startsWith(route)

                return (
                  <NavLink
                    key={key}
                    to={route}
                    title={collapsed ? t(`nav.${key}`) : undefined}
                    className={`sidebar-item group ${isActive ? 'active' : ''}`}
                    style={isActive ? {
                      background: 'rgba(255,255,255,0.15)',
                      color: '#fff',
                    } : {
                      color: 'rgba(255,255,255,0.6)',
                    }}
                  >
                    <Icon size={18} style={{ flexShrink: 0 }} />
                    {!collapsed && (
                      <span className="truncate text-sm">{t(`nav.${key}`)}</span>
                    )}
                    {isActive && !collapsed && (
                      <span className="mr-auto w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#FF6D00' }} />
                    )}
                  </NavLink>
                )
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User section */}
      <div
        className="p-3 flex-shrink-0"
        style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}
      >
        {/* User info */}
        {!collapsed && (
          <div
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl mb-2"
            style={{ background: 'rgba(255,255,255,0.06)' }}
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"
              style={{ background: '#FF6D00' }}
            >
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="overflow-hidden flex-1">
              <div
                className="text-white font-bold text-sm truncate"
                style={{ fontFamily: 'Cairo, sans-serif' }}
              >
                {user?.name || 'المدير'}
              </div>
              <div
                className="text-xs truncate"
                style={{ color: 'rgba(255,255,255,0.4)', fontFamily: 'Tajawal, sans-serif' }}
              >
                {user?.email || ''}
              </div>
            </div>
          </div>
        )}

        {/* Logout */}
        <button
          onClick={logout}
          className="sidebar-item w-full"
          style={{ color: 'rgba(255,255,255,0.5)' }}
          title={collapsed ? t('nav.logout') : undefined}
        >
          <LogOut size={18} style={{ flexShrink: 0 }} />
          {!collapsed && <span>{t('nav.logout')}</span>}
        </button>
      </div>
    </aside>
  )
}
